import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import LayoutThumb from './LayoutThumb2';
import { CHARCOAL, MUTED, GREEN, BORDER } from '../../../theme/tokens2';

// ─── RoomUsePanel ───────────────────────────────────────────────────────────
// Renders a room's uses as a "catalog, not a wizard": every use is shown
// as a pill up front (never hidden behind a click, unlike an accordion),
// with exactly one thing highlighted at a time -- either one room-use
// pill, or the Empty pill. Clicking a room-use pill swaps which use's
// thumbnail strip is shown below (and hides it entirely while Empty is
// the one highlighted); clicking Empty hides the thumbnail strip
// altogether.
//
// A single-use room still shows its one use as a pill -- same visual
// chip, same rounded/bordered treatment, and now the same click behaviour
// as a multi-use tab, toggling its thumbnail strip on and off against
// Empty. This keeps "a pill means a room-use" a consistent visual
// language regardless of count, rather than teaching the eye that pills
// are sometimes buttons and sometimes static labels.
//
// Highlighting is driven by ONE piece of local state, `activeUseName`:
// a real room-use name highlights that pill and shows its thumbnails;
// `null` highlights Empty and shows no thumbnails. This is what makes
// "click a room-use pill -> Empty un-highlights and that pill highlights"
// work automatically -- there's only one flag deciding who's lit up, not
// two that can drift apart.
//
// `activeUseName` starts out matching the room's real saved choice (see
// the useState initializer below), but after that it's free to diverge
// from it while browsing: clicking a room-use pill only changes what's
// being LOOKED AT, it doesn't save anything by itself (there's no
// layoutOptionId yet to save -- see onSelectLayout's signature). Clicking
// Empty is different: since Empty has no further "which layout" step, it
// saves immediately, the same way clicking a specific layout thumbnail
// does. Only picking Empty or an actual layout thumbnail ever touches the
// backend; browsing room-use pills is free to click around without
// saving anything until one of those two happens.
//
// Empty -- exactly one thing is always "chosen" for a room with any
// room-uses at all: either a real furnished layout, or Empty. Empty
// isn't a real database row (see RoomDto.selection's javadoc on the
// backend) -- it's just what's shown whenever room.selection has no
// real roomUseName/layoutOptionId, which is also the state a room
// starts in before anyone's touched it. That's why Empty is the default
// starting point for `activeUseName` too, with no extra plumbing needed.
//
// Props:
//   room           — a RoomDto-shaped object (roomUses[], selection,
//                     footprintPolygon, wallSegments)
//   furniturePieces — the global FurniturePieceDto[] catalog, threaded
//                      through to LayoutThumb so it can resolve each
//                      placement's stencil image/dimensions by name
//   pxPerMm         — project-wide mm-to-px scale factor, threaded through
//                      to LayoutThumb so each room's thumbnail is sized by
//                      its own real dimensions (see index.jsx)
//   onSelectLayout  — fn(roomUseName, layoutOptionId) -- called with
//                      (null, null) to select Empty, since Empty is just
//                      "no real selection" rather than a real id to send

const PILL_BASE_SX = {
  fontSize: '12.5px',
  borderRadius: '999px',
  padding: '6px 14px',
  whiteSpace: 'nowrap',
};

export default function RoomUsePanel({ room, furniturePieces, pxPerMm, onSelectLayout }) {
  const useNames = room.roomUses;
  const [activeUseName, setActiveUseName] = useState(() => {
    // selectedRoomUseName is null whenever Empty is the current choice
    // (see the Empty note above) -- that already means "start on Empty",
    // which is exactly what falling through to null below gives us. The
    // only other case to guard against is a saved selection pointing at
    // a room-use that no longer exists for this room (e.g. the pipeline
    // regenerated and this use disappeared) -- treated as Empty too,
    // rather than silently landing on some other, unrelated tab.
    const selected = room.selection?.selectedRoomUseName;
    const isRealUse = useNames.some((u) => u.name === selected);
    return isRealUse ? selected : null;
  });

  // A room with no room-uses at all -- nothing was matched for it on the
  // FRUITS tab (see koordinator5.py), so there are no pills or layout
  // thumbnails to show, and no Empty pill either (there's nothing to be
  // empty OF). Checked after the useState call above so hooks stay in
  // the same order on every render, even though this branch never needs
  // activeUseName. This mirrors the hatched-fill treatment FloorplanCanvas
  // gives the same room on the plan.
  if (useNames.length === 0) {
    return (
      <Box>
        <Typography sx={{ fontSize: '15px', fontWeight: 600, color: CHARCOAL, mb: 1 }}>
          {room.label}
        </Typography>
        <Typography sx={{ fontSize: '13px', color: MUTED, lineHeight: 1.55 }}>
          No layout options for this room yet — discuss your options with your contractor.
        </Typography>
      </Box>
    );
  }

  // Empty is active whenever activeUseName is null -- see the file header
  // for why this one flag is the single source of truth for both the
  // pill highlighting AND whether the thumbnail strip shows at all.
  const isEmptyActive = activeUseName === null;
  const activeUse = useNames.find((u) => u.name === activeUseName) ?? null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mb: 1.75 }}>
        <Typography sx={{ fontSize: '15px', fontWeight: 600, color: CHARCOAL }}>{room.label}</Typography>
        <Typography sx={{ fontSize: '11.5px', color: 'rgba(0,0,0,0.62)' }}>
          {useNames.length} use{useNames.length > 1 ? 's' : ''} to try
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap', mb: 1.5 }}>
        {useNames.length === 1 ? (
          <Box
            component="button"
            onClick={() => setActiveUseName(useNames[0].name)}
            sx={{
              ...PILL_BASE_SX,
              fontWeight: isEmptyActive ? 500 : 600,
              color: isEmptyActive ? 'rgba(0,0,0,0.68)' : GREEN,
              background: isEmptyActive ? '#fff' : 'rgba(33,131,51,0.08)',
              border: `1px solid ${isEmptyActive ? BORDER : 'rgba(33,131,51,0.35)'}`,
              cursor: 'pointer',
              transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
              '&:hover': isEmptyActive
                ? { borderColor: 'rgba(0,0,0,0.32)', color: CHARCOAL, background: 'rgba(0,0,0,0.02)' }
                : {},
            }}
          >
            {useNames[0].name}
          </Box>
        ) : (
          <Box
            role="tablist"
            aria-label={`${room.label} uses`}
            sx={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}
          >
            {useNames.map((roomUse) => {
              // activeUseName is the single source of truth for which
              // pill is highlighted now (see the file header) -- no
              // separate "is this the real saved selection" check
              // needed, so a tab lights up the moment it's clicked.
              const isActive = roomUse.name === activeUseName;
              return (
                <Box
                  key={roomUse.name}
                  component="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveUseName(roomUse.name)}
                  sx={{
                    ...PILL_BASE_SX,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? GREEN : 'rgba(0,0,0,0.68)',
                    background: isActive ? 'rgba(33,131,51,0.08)' : '#fff',
                    border: `1px solid ${isActive ? 'rgba(33,131,51,0.35)' : BORDER}`,
                    cursor: 'pointer',
                    transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
                    '&:hover': isActive
                      ? {}
                      : { borderColor: 'rgba(0,0,0,0.32)', color: CHARCOAL, background: 'rgba(0,0,0,0.02)' },
                  }}
                >
                  {roomUse.name}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Empty is a real, complete choice with no further "which layout"
            step, so unlike a room-use pill above, clicking it BOTH
            highlights locally (setActiveUseName(null), hiding the
            thumbnail strip and un-highlighting whatever room-use pill was
            lit) AND saves immediately (onSelectLayout(null, null)) --
            see the file header for why room-use pills don't save on
            their own but this one does. Shown unconditionally here (no
            id to check for). Clicking it only ever SELECTS: if it's
            already the active choice, the click is a no-op rather than
            clearing back to nothing, since "nothing selected" isn't a
            state this panel allows. */}
        <Box
          component="button"
          onClick={() => {
            if (!isEmptyActive) {
              setActiveUseName(null);
              onSelectLayout(null, null);
            }
          }}
          sx={{
            ...PILL_BASE_SX,
            fontWeight: isEmptyActive ? 600 : 500,
            color: isEmptyActive ? GREEN : 'rgba(0,0,0,0.68)',
            background: isEmptyActive ? 'rgba(33,131,51,0.08)' : '#fff',
            border: `1px solid ${isEmptyActive ? 'rgba(33,131,51,0.35)' : BORDER}`,
            cursor: 'pointer',
            transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
            '&:hover': isEmptyActive
              ? {}
              : { borderColor: 'rgba(0,0,0,0.32)', color: CHARCOAL, background: 'rgba(0,0,0,0.02)' },
          }}
        >
          Empty
        </Box>
      </Box>

      {activeUse && (
        <Box
          key={activeUse.name}
          role="tabpanel"
          sx={{
            '@keyframes roomUseFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
            animation: 'roomUseFadeIn 0.15s ease',
          }}
        >
          <ThumbStrip
            roomUse={activeUse}
            room={room}
            furniturePieces={furniturePieces}
            pxPerMm={pxPerMm}
            onSelectLayout={onSelectLayout}
          />
        </Box>
      )}
    </Box>
  );
}

function ThumbStrip({ roomUse, room, furniturePieces, pxPerMm, onSelectLayout }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {roomUse.layoutOptions.map((layoutOption, index) => {
        const isActive =
          room.selection?.selectedRoomUseName === roomUse.name &&
          room.selection?.selectedLayoutOptionId === layoutOption.id;
        return (
          <LayoutThumb
            key={layoutOption.id}
            layoutOption={layoutOption}
            furniturePieces={furniturePieces}
            footprintPolygon={room.footprintPolygon}
            wallSegments={room.wallSegments}
            pxPerMm={pxPerMm}
            label={`Layout ${index + 1}`}
            isActive={isActive}
            // Only ever selects -- clicking the already-active thumbnail
            // is a no-op rather than clearing it, matching the same
            // always-something-selected rule the room-use/Empty pills
            // above follow. To move off a furnished layout, the resident
            // clicks a different one, or the Empty pill.
            onSelect={() => {
              if (!isActive) onSelectLayout(roomUse.name, layoutOption.id);
            }}
          />
        );
      })}
    </Box>
  );
}